/**
 * JWT parsing utilities for extracting token metadata
 */

export interface JWTPayload {
  exp?: number; // Expiration time (seconds since epoch)
  iat?: number; // Issued at time
  sub?: string; // Subject (user ID)
  iss?: string; // Issuer
  aud?: string; // Audience
  [key: string]: unknown; // Allow additional claims
}

/**
 * Decode a JWT without verification (client-side only)
 * WARNING: This does NOT verify the signature - only for reading metadata
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      console.error("Invalid JWT format");
      return null;
    }

    const payload = parts[1];
    const decoded = base64UrlDecode(payload);
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

/**
 * Get expiration timestamp from JWT (in milliseconds)
 */
export function getJWTExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000; // Convert seconds to milliseconds
}

/**
 * Check if a JWT is expired (with optional buffer in seconds)
 */
export function isJWTExpired(token: string, bufferSeconds = 300): boolean {
  const expiration = getJWTExpiration(token);
  if (!expiration) return false; // No expiry = doesn't expire
  const now = Date.now();
  const buffer = bufferSeconds * 1000;
  return expiration - buffer < now;
}

/**
 * Base64-URL decode (RFC 4648)
 */
function base64UrlDecode(str: string): string {
  // Replace URL-safe chars and add padding
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  while (base64.length % 4) {
    base64 += "=";
  }
  return atob(base64);
}
